import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'router';
import { ClickTracker } from 'components/Tracking';
import { CloseIcon } from 'components/Icons';
import { MENU_ICON_SIZE } from './navigationUtils';
import { Text } from 'components';
import { useI18n } from 'components/I18n';
import { MENU_ITEM_TYPES, MENU_LINK_TARGET_TYPES } from 'utils/constants';
import * as pageActions from 'actions/page';
import { resetScrollableOffset } from 'actions/scrollable';
import { MenuContainer, MenuLink, Nav, CloseButton, SignUpButton } from './Styles';


function TopNavigation(props) {
  const {
    position,
    hideItemsForUnentitled,
    innerRef,
    isExpanded,
    onCloseMenu,
    shouldShowSignUp,
    visibleMenuItems,
    defaultMenuItem,
  } = props;

  const {
    location,
    page,
    collectionRefreshThreshold,
  } = useSelector(state => ({
    location: state.router.location,
    page: state.page,
    collectionRefreshThreshold: state.settings.features.collectionRefreshThresholdInMin
      ? state.settings.features.collectionRefreshThresholdInMin * 60 * 1000
      : undefined,
  }), shallowEqual);

  const dispatch = useDispatch();
  const router = useRouter();
  const i18n = useI18n();

  useEffect(() => {
    const identifier = location.name === 'home'
      ? defaultMenuItem?.identifier
      : location.params.id;
    if (identifier && !page[identifier]) {
      dispatch(pageActions.UpdatePage({
        id: identifier,
        lastViewDate: new Date(),
        isExpired: false,
      }));
    }
  }, [location.name, defaultMenuItem, page, dispatch]);

  const closeVerticalMenu = () => {
    if (position === 'vertical' && onCloseMenu) {
      onCloseMenu();
    }
  };

  const handleContentListMenuClick = (item) => {
    const now = new Date();
    const isExpired = page[item.identifier] && !page[item.identifier].isExpired
    && collectionRefreshThreshold
      ? (now - page[item.identifier].lastViewDate) > collectionRefreshThreshold
      : false;
    if (isExpired) {
      dispatch(pageActions.UpdatePage({
        id: item.identifier,
        lastViewDate: new Date(),
        isExpired: true,
      }));
    }

    dispatch(resetScrollableOffset(item.identifier));
    closeVerticalMenu();
  };

  const renderMenuItems = () => {
    const currentUrl = router.getUrl(location, false);

    return visibleMenuItems.map((item) => {
      let menuLink;

      if (defaultMenuItem === item) { // home
        menuLink = (
          <MenuLink
            key="home"
            to={{ name: 'home' }}
            data-content={item.title}
            active={location.name === 'home'}
            position={position}
            onClick={() => {
              if (item.type === MENU_ITEM_TYPES.contentList) {
                handleContentListMenuClick(item);
              } else {
                closeVerticalMenu();
              }
            }}
          >
            {item.title}
          </MenuLink>
        );
      } else if (item.type === MENU_ITEM_TYPES.link) {
        if (item.linkTarget === MENU_LINK_TARGET_TYPES.webview) {
          menuLink = (
            <MenuLink
              key={`webview-${item._id}`}
              to={{ name: 'webview', params: { id: item._id } }}
              data-content={item.title}
              active={location.name === 'webview' && location.params.id === item._id}
              position={position}
              onClick={closeVerticalMenu}
            >
              {item.title}
            </MenuLink>
          );
        } else {
          const target = item.linkTarget === MENU_LINK_TARGET_TYPES.blank ? '_blank' : undefined;
          menuLink = (
            <MenuLink
              key={item.url}
              href={item.url}
              target={target}
              data-content={item.title}
              active={currentUrl === item.url}
              position={position}
              onClick={closeVerticalMenu}
            >
              {item.title}
            </MenuLink>
          );
        }
      } else if (item.type === MENU_ITEM_TYPES.epg) {
        menuLink = (
          <MenuLink
            key="epg"
            to={{ name: 'epg' }}
            data-content={item.title}
            active={location.name === 'epg'}
            position={position}
            onClick={closeVerticalMenu}
          >
            {item.title}
          </MenuLink>
        );
      } else if (item.type === MENU_ITEM_TYPES.contentList) {
        menuLink = (
          <MenuLink
            key={`collections-${item.identifier}`}
            to={{ name: 'collections', params: { id: item.identifier } }}
            data-content={item.title}
            active={location.name === 'collections' && location.params.id === item.identifier}
            position={position}
            onClick={() => handleContentListMenuClick(item)}
          >
            {item.title}
          </MenuLink>
        );
      }

      if (!menuLink) {
        return null;
      }

      return (
        <ClickTracker
          key={item.title}
          component="Menu"
          clickType="navigation"
          eventName={item.trackingEvent}
          collectionId={item.identifier}
        >
          {menuLink}
        </ClickTracker>
      );
    }).filter(Boolean);
  };

  return (
    <Nav
      position={position}
      isExpanded={isExpanded}
      data-cy={`top-navigation-${position}`}
      {...(innerRef ? { innerRef } : {})}
    >
      {position === 'vertical' && onCloseMenu && (
        <CloseButton onClick={onCloseMenu}>
          <CloseIcon size={MENU_ICON_SIZE} />
        </CloseButton>
      )}
      <MenuContainer position={position}>
        {!hideItemsForUnentitled && renderMenuItems()}
        {shouldShowSignUp && (
          <ClickTracker
            component="Menu"
            clickType="navigation"
            eventName="SignUp"
          >
            <SignUpButton
              to={{ name: 'create-account' }}
              data-content={i18n.formatText('menu.signUp')}
              active={location.name === 'create-account'}
              position={position}
              onClick={closeVerticalMenu}
            >
              <Text id="menu.signUp" />
            </SignUpButton>
          </ClickTracker>
        )}
      </MenuContainer>
    </Nav>
  );
}

TopNavigation.propTypes = {
  position: PropTypes.oneOf(['vertical', 'horizontal']).isRequired,
  hideItemsForUnentitled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  innerRef: PropTypes.object,
  onCloseMenu: PropTypes.func,
  shouldShowSignUp: PropTypes.bool,
  visibleMenuItems: PropTypes.array.isRequired,
  defaultMenuItem: PropTypes.object,
};

export default React.memo(TopNavigation);
